import type { ConceptCategory } from './types';
import { translate } from '../i18n/i18n';

export const CATEGORY_CODE: Record<ConceptCategory, string> = {
  Receptive: 'REC',
  Expressive: 'EXP',
  Echoic: 'ECH',
  Intraverbal: 'INT',
  Mand: 'MND',
  Matching: 'MAT',
  Sorting: 'SRT',
  Sequencing: 'SEQ',
  Math: 'MTH',
  Literacy: 'LIT',
  'Motor imitation': 'MOT',
  Social: 'SOC',
  'Daily living': 'DLY',
};

export const KIND_CODE: Record<string, string> = {
  tileMatch: 'TAP',
  supervisorScored: 'SAY',
  sequenceTap: 'ORD',
  timerHold: 'HLD',
  labelParts: 'LBL',
  findPart: 'TCH',
  findInScene: 'SPY',
  studio: 'STU',
  traceIt: 'TRC',
  placeIt: 'PUT',
  sortBins: 'BIN',
  yesNo: 'YN',
  oddOneOut: 'ODD',
  findAll: 'ALL',
  memoryPairs: 'MEM',
  slotOrder: 'BLD',
  pictureSentence: 'PIC',
  blockCopy: 'CPY',
  taskChain: 'STP',
  waitForGo: 'WT',
  rotateFind: '3D',
};

const SKIP = new Set([
  'and', 'or', 'the', 'a', 'an', 'of', 'to', 'in', 'on', 'with', 'by', 'for', 'at', 'it', 'is', '3d', 'vs',
  'match', 'sort', 'sequence', 'imitate', 'mand', 'echoic', 'intraverbal', 'receptive', 'expressive',
]);

type Item = { id: string; label: string; category: ConceptCategory; kind: string };

export function topicOf(label: string, category: string, kind: string): string {
  const after = label.includes(':') ? label.slice(label.indexOf(':') + 1) : label;
  const stems = category.toLowerCase().split(' ').map((w) => w.slice(0, 4));
  const words = after
    .replace(/\([^)]*\)/g, ' ')
    .split(/[^A-Za-z0-9]+/)
    .filter((w) => w && !SKIP.has(w.toLowerCase()) && !stems.some((s) => w.toLowerCase().startsWith(s)))
    .map((w) => w.toUpperCase());
  if (words.length === 0) return KIND_CODE[kind] ?? 'GEN';
  if (words.length === 1) return words[0].slice(0, 5);
  if (words.length === 2) return (words[0].slice(0, 4) + words[1].slice(0, 3)).slice(0, 7);
  return words.slice(0, 4).map((w) => w[0]).join('');
}

const baseOf = (c: Item) => `${CATEGORY_CODE[c.category] ?? 'GEN'}-${topicOf(c.label, c.category, c.kind)}`;

export function codesFor(items: Item[], fixed: Record<string, string> = {}): Record<string, string> {
  const out: Record<string, string> = {};
  const taken = new Set(Object.values(fixed));
  const fresh = items.filter((c) => !fixed[c.id]).sort((a, b) => a.id.localeCompare(b.id));
  const groups = new Map<string, Item[]>();
  for (const c of fresh) groups.set(baseOf(c), [...(groups.get(baseOf(c)) ?? []), c]);
  for (const [base, group] of groups) {
    const shared = group.length > 1 || taken.has(base);
    for (const c of group) {
      let code = shared ? `${base}-${KIND_CODE[c.kind] ?? 'X'}` : base;
      for (let n = 2; taken.has(code); n++) code = `${shared ? `${base}-${KIND_CODE[c.kind] ?? 'X'}` : base}${n}`;
      taken.add(code);
      out[c.id] = code;
    }
  }
  return { ...fixed, ...out };
}

const KIND_NAME: Record<string, string> = {
  tileMatch: 'tap the picture',
  supervisorScored: 'say it, adult scores',
  sequenceTap: 'tap in order',
  timerHold: 'hold for a time',
  labelParts: 'label the picture',
  findPart: 'touch the part',
  findInScene: 'find it in the picture',
  studio: 'made in Studio',
  traceIt: 'trace or write it',
  placeIt: 'put it in place',
  sortBins: 'sort into groups',
  yesNo: 'yes or no',
  oddOneOut: 'odd one out',
  findAll: 'find them all',
  memoryPairs: 'memory pairs',
  slotOrder: 'build it',
  blockCopy: 'copy the pattern',
  taskChain: 'step by step',
  waitForGo: 'wait for go',
  rotateFind: 'turn and find in 3D',
};

export function explainCode(code: string, label: string, category: ConceptCategory, kind: string): string {
  const [cat, topic, ...rest] = code.split('-');
  const tail = rest.join('-');
  const lines = [
    translate('{code} = {category} (the category)', { code: cat, category: translate(category) }),
    topic === KIND_CODE[kind]
      ? translate('{code} = {kind} (the type of game)', { code: topic, kind: KIND_NAME[kind] ? translate(KIND_NAME[kind]) : kind })
      : translate('{code} = short for "{label}"', { code: topic, label: label.includes(':') ? label.slice(label.indexOf(':') + 1).trim() : label }),
  ];
  if (tail) {
    const digits = tail.match(/\d+$/)?.[0];
    const kindPart = digits ? tail.slice(0, -digits.length) : tail;
    if (kindPart) lines.push(translate('{code} = {kind} (the type of game, because other games share this topic)', { code: kindPart, kind: KIND_NAME[kind] ? translate(KIND_NAME[kind]) : kind }));
    if (digits) lines.push(translate('{code} = a number to keep the code unique', { code: digits }));
  }
  return lines.join('\n');
}
